/** Verified role session lives only in memory. Redemption must verify it server-side first. */
import { create } from "zustand";
import { ApiError, createApiClient } from "@/api/client";
export interface JudgeSession {
  competitionId: string;
  token: string;
  principalKey: string;
  queuePrincipalKey?: string;
  roles: string[];
  areaId?: string;
  seat?: number;
  expiresAt: string;
  kind?: "judge" | "starter" | "announcer";
  competitionName?: string;
  competitionSlug?: string;
  timezone?: string;
}
const allowed = new Set(["judge", "starter", "head_judge", "chief_judge", "announcer"]);
export function validateJudgeSession(input: JudgeSession, now = Date.now()): JudgeSession {
  if (
    !input ||
    typeof input !== "object" ||
    typeof input.competitionId !== "string" ||
    !input.competitionId ||
    typeof input.token !== "string" ||
    !input.token ||
    /[\r\n]/.test(input.token) ||
    typeof input.principalKey !== "string" ||
    !input.principalKey ||
    !Array.isArray(input.roles) ||
    !input.roles.length ||
    input.roles.some((role) => typeof role !== "string" || !allowed.has(role)) ||
    typeof input.expiresAt !== "string" ||
    !Number.isFinite(Date.parse(input.expiresAt)) ||
    Date.parse(input.expiresAt) <= now ||
    (input.areaId !== undefined && typeof input.areaId !== "string") ||
    (input.seat !== undefined &&
      (!Number.isInteger(input.seat) || input.seat < 1 || input.seat > 16))
  )
    throw new Error("Invalid or expired role session");
  return {
    competitionId: input.competitionId,
    token: input.token,
    principalKey: input.principalKey,
    ...(input.queuePrincipalKey ? { queuePrincipalKey: input.queuePrincipalKey } : {}),
    roles: [...new Set(input.roles)],
    expiresAt: input.expiresAt,
    ...(input.kind ? { kind: input.kind } : {}),
    ...(input.competitionName ? { competitionName: input.competitionName } : {}),
    ...(input.competitionSlug ? { competitionSlug: input.competitionSlug } : {}),
    ...(input.timezone ? { timezone: input.timezone } : {}),
    ...(input.areaId ? { areaId: input.areaId } : {}),
    ...(input.seat ? { seat: input.seat } : {}),
  };
}
interface State {
  session: JudgeSession | null;
  setVerifiedSession: (session: JudgeSession) => void;
  clear: (competitionId?: string) => void;
}
export const useJudgeSession = create<State>((set) => ({
  session: null,
  setVerifiedSession: (input) => {
    const session = validateJudgeSession(input);
    set({ session });
  },
  clear: (competitionId) =>
    set((state) =>
      !competitionId || state.session?.competitionId === competitionId ? { session: null } : state,
    ),
}));
export function currentJudgeSession(competitionId: string): JudgeSession | null {
  const session = useJudgeSession.getState().session;
  if (!session || session.competitionId !== competitionId) return null;
  if (Date.parse(session.expiresAt) <= Date.now()) {
    useJudgeSession.getState().clear(competitionId);
    return null;
  }
  return session;
}
/** Queue/socket callbacks stay bound to their original verified session epoch. */
export function judgeSessionToken(competitionId: string, principalKey: string): string | null {
  const session = currentJudgeSession(competitionId);
  return session?.principalKey === principalKey ? session.token : null;
}
export function judgeSessionClient(
  competitionId: string,
  boundPrincipal = currentJudgeSession(competitionId)?.principalKey,
) {
  const identity = () => {
    const s = currentJudgeSession(competitionId);
    return s ? `${s.principalKey}:${s.token}` : null;
  };
  const client = createApiClient(async () => {
    const session = currentJudgeSession(competitionId);
    if (!session) throw new ApiError(401, "Reopen your role link to sign in");
    if (session.principalKey !== boundPrincipal)
      throw new ApiError(409, "Your role session changed. Reopen the workspace.", "stale_session");
    return session.token;
  }, identity);
  const guarded = <T>(request: Promise<T>): Promise<T> =>
    request.catch((error: unknown) => {
      if (
        error instanceof ApiError &&
        error.status === 401 &&
        currentJudgeSession(competitionId)?.principalKey === boundPrincipal
      )
        useJudgeSession.getState().clear(competitionId);
      throw error;
    });
  return {
    ...client,
    download: (...args: Parameters<typeof client.download>) => guarded(client.download(...args)),
    upload: <T>(...args: Parameters<typeof client.upload<T>>) => guarded(client.upload<T>(...args)),
    get: <T>(...args: Parameters<typeof client.get<T>>) => guarded(client.get<T>(...args)),
    post: <T>(...args: Parameters<typeof client.post<T>>) => guarded(client.post<T>(...args)),
    put: <T>(...args: Parameters<typeof client.put<T>>) => guarded(client.put<T>(...args)),
    delete: <T>(...args: Parameters<typeof client.delete<T>>) => guarded(client.delete<T>(...args)),
  };
}
